export const SETTINGS_TAB = 'settings';
export const DEFAULT_SECTION = 'import-behavior';
export const REF_SECTION_PREFIX = 'ref:';
export const SUPPLIER_TIERS_SECTION = 'supplier-tiers';
export const AUDIT_LOG_SECTION = 'audit-log';
export const REFERENCE_LISTS_LEGACY_SECTION = 'reference-lists';
export const DEFAULT_TOP_TAB = 'manual-import';
export const WIDGET_ACCESS_SECTION = 'widget-access';

export const LEGACY_TAB_TO_SECTION: Record<string, string> = {
  'scope-rules': 'scope-rules',
  'job-schedules': 'job-schedules',
  'audit-log': AUDIT_LOG_SECTION,
  'reference-lists': REFERENCE_LISTS_LEGACY_SECTION,
  settings: DEFAULT_SECTION,
};

export const DATA_MANAGEMENT_TOP_TABS = [
  { key: 'manual-import', label: 'Manual Import' },
  { key: 'import-jobs', label: 'Import Jobs' },
  { key: SETTINGS_TAB, label: 'Settings' },
] as const;

export interface SettingsNavItem {
  id: string;
  label: string;
}

export interface SettingsNavGroup {
  id: string;
  label: string;
  items: SettingsNavItem[];
}

export interface SettingsPaneProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export const STATIC_NAV_GROUPS: SettingsNavGroup[] = [
  {
    id: 'import',
    label: 'Import',
    items: [
      { id: DEFAULT_SECTION, label: 'Import Behavior' },
      { id: 'scope-rules', label: 'Scope Rules' },
    ],
  },
  {
    id: 'automation',
    label: 'Automation',
    items: [
      { id: 'timing-thresholds', label: 'Timing Thresholds' },
      { id: 'job-schedules', label: 'Scheduled Jobs' },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    items: [{ id: WIDGET_ACCESS_SECTION, label: 'Widget Access' }],
  },
];

export const KNOWN_CATEGORY_ORDER = ['status', 'reason', 'classification'];

export const KNOWN_CATEGORY_LABELS: Record<string, string> = {
  status: 'Statuses',
  reason: 'Reasons',
  classification: 'Classifications',
};

export function formatListKey(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatCategoryLabel(category: string): string {
  if (KNOWN_CATEGORY_LABELS[category]) {
    return KNOWN_CATEGORY_LABELS[category];
  }

  return category
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function toRefSection(listKey: string): string {
  return `${REF_SECTION_PREFIX}${listKey}`;
}

export function fromRefSection(section: string): string | null {
  if (!section.startsWith(REF_SECTION_PREFIX)) {
    return null;
  }
  return section.slice(REF_SECTION_PREFIX.length);
}

export function isLegacySettingsTab(tab: string | null): boolean {
  return tab !== null && tab in LEGACY_TAB_TO_SECTION && tab !== SETTINGS_TAB;
}

export function resolveTopTab(requestedTab: string | null): string {
  if (requestedTab && DATA_MANAGEMENT_TOP_TABS.some((tab) => tab.key === requestedTab)) {
    return requestedTab;
  }
  if (isLegacySettingsTab(requestedTab) || requestedTab === SETTINGS_TAB) {
    return SETTINGS_TAB;
  }
  return DEFAULT_TOP_TAB;
}

export function resolveLegacySection(requestedTab: string | null, currentSection: string | null): string {
  if (requestedTab && isLegacySettingsTab(requestedTab)) {
    return LEGACY_TAB_TO_SECTION[requestedTab];
  }
  if (currentSection) {
    return currentSection;
  }
  if (requestedTab === SETTINGS_TAB) {
    return DEFAULT_SECTION;
  }
  return DEFAULT_SECTION;
}

export interface ReferenceListNavSource {
  key: string;
  category: string;
}

export function buildSettingsNavGroups(lists: ReferenceListNavSource[]): SettingsNavGroup[] {
  const groups: SettingsNavGroup[] = STATIC_NAV_GROUPS.map((group) => ({
    ...group,
    items: [...group.items],
  }));

  const categories = new Set(lists.map((list) => list.category));
  const orderedCategories = [
    ...KNOWN_CATEGORY_ORDER.filter((category) => categories.has(category)),
    ...Array.from(categories).filter((category) => !KNOWN_CATEGORY_ORDER.includes(category)).sort(),
  ];

  for (const category of orderedCategories) {
    const items = lists
      .filter((list) => list.category === category)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((list) => ({ id: toRefSection(list.key), label: formatListKey(list.key) }));

    if (items.length > 0) {
      groups.push({
        id: `category-${category}`,
        label: formatCategoryLabel(category),
        items,
      });
    }
  }

  groups.push({
    id: 'supplier-tiers',
    label: 'Supplier Tiers',
    items: [{ id: SUPPLIER_TIERS_SECTION, label: 'Supplier Tiers' }],
  });

  groups.push({
    id: 'activity',
    label: 'Activity',
    items: [{ id: AUDIT_LOG_SECTION, label: 'Audit Log' }],
  });

  return groups;
}

export function resolveDefaultRefSection(lists: ReferenceListNavSource[]): string {
  const statusLists = lists
    .filter((list) => list.category === 'status')
    .sort((a, b) => a.key.localeCompare(b.key));

  if (statusLists[0]) {
    return toRefSection(statusLists[0].key);
  }
  if (lists[0]) {
    return toRefSection(lists[0].key);
  }
  return SUPPLIER_TIERS_SECTION;
}

export function filterNavGroups(groups: SettingsNavGroup[], search: string): SettingsNavGroup[] {
  const query = search.trim().toLowerCase();
  if (!query) {
    return groups;
  }

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.label.toLowerCase().includes(query)),
    }))
    .filter((group) => group.items.length > 0);
}
